import React from "react";
import useConfig from "../../demos/hooks/useConfig";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import EventSystem from "../../events/EventSystem";

/**
 * The tree the folder pickers browse: the frontend's whole content root, not
 * just its drawings.
 *
 * `root=content` is what separates this from the folder right panel — the
 * pickers have to reach json/application and svg, which sit beside drawings/.
 * On a hand-configured install (serverSubFolder) the tree is then narrowed to
 * that subfolder, so the customer browses their own files and not the layout
 * of the server.
 *
 * Shared by the Folder Structure and AI Setup panels, which show the same three
 * folders and must therefore browse the same tree.
 */
export function useContentFolderTree(enabled: boolean) {
  const { config } = useConfig();
  const { user } = React.useContext(CadviewerContext);
  const { ServerBackEndUrl } = config;

  const [folderStructure, setFolderStructure] = React.useState<any[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const fetchFolderStructure = React.useCallback(async () => {
    try {
      let endpoint = "listdwgdirectory";
      if (!ServerBackEndUrl || !ServerBackEndUrl.endsWith("/")) {
        endpoint = "/" + endpoint;
      }
      const baseUrl = ServerBackEndUrl || "http://localhost:3000";

      const headers = new Headers();
      if (user?.token && config.enableLoginFeature) {
        headers.append("Authorization", `Bearer ${user.token}`);
      }
      headers.append("Content-Type", "application/json");
      headers.append("Accept", "application/json");

      const response = await fetch(
        baseUrl + endpoint + `?loginFeatureEnabled=${config.enableLoginFeature}&root=content`,
        { method: "GET", headers }
      );
      if (!response.ok) throw new Error(`Status ${response.status}`);

      setFolderStructure(await response.json());
      setError(null);
    } catch (err: any) {
      console.error("Failed to load folder structure:", err);
      setError("Failed to load folder structure.");
    }
  }, [ServerBackEndUrl, user?.token, config.enableLoginFeature]);

  React.useEffect(() => {
    if (enabled) fetchFolderStructure();
  }, [enabled, fetchFolderStructure]);

  React.useEffect(() => {
    const handleRefresh = () => fetchFolderStructure();
    EventSystem.subscribe("panel.folder_list.refresh", handleRefresh);
    return () => EventSystem.unsubscribe("panel.folder_list.refresh", handleRefresh);
  }, [fetchFolderStructure]);

  const filteredFolderStructure = React.useMemo(() => {
    if (!config.serverSubFolder) return folderStructure;

    const normalized = config.serverSubFolder.endsWith("/")
      ? config.serverSubFolder.slice(0, -1)
      : config.serverSubFolder;
    const targetFolderName = config.serverSubFolder.split("/").filter(Boolean).pop();

    const normPath = (p: string) => p.replace(/\\/g, "/");
    const findByPath = (list: any[]): any | null => {
      for (const folder of list) {
        if (folder.path && normPath(folder.path).endsWith(normalized)) return folder;
        if (folder.children) {
          const found = findByPath(folder.children);
          if (found) return found;
        }
      }
      return null;
    };
    const findByName = (list: any[]): any | null => {
      for (const folder of list) {
        if (folder.name === targetFolderName) return folder;
        if (folder.children) {
          const found = findByName(folder.children);
          if (found) return found;
        }
      }
      return null;
    };

    const target = findByPath(folderStructure) ?? findByName(folderStructure);
    return target?.children || [];
  }, [folderStructure, config.serverSubFolder]);

  return { folderStructure: filteredFolderStructure, error, refresh: fetchFolderStructure };
}
